/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState, useEffect } from 'react';
import { Button, Card, Typography, Space, Steps } from 'antd';
import { DownloadOutlined, MobileOutlined, CloseOutlined, ShareAltOutlined, PlusOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detectar iOS
    const detectIOS = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    };

    // Verificar si la app ya está instalada
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppiOS = window.navigator.standalone === true;
      const isInWebAppChrome = window.matchMedia('(display-mode: standalone)').matches;
      
      return isStandalone || isInWebAppiOS || isInWebAppChrome;
    };

    const iosDetected = detectIOS();
    const installedStatus = checkIfInstalled();
    
    setIsIOS(iosDetected);
    setIsInstalled(installedStatus);

    if (installedStatus) {
      return;
    }

    // Para iOS Safari, mostrar instrucciones directamente
    if (iosDetected && !installedStatus) {
      setTimeout(() => {
        setIsVisible(true);
      }, 3000);
    }

    // Escuchar el evento beforeinstallprompt (solo en Chrome/Edge)
    const handleBeforeInstallPrompt = (event) => {
      // Prevenir que Chrome 67 y anteriores muestren automáticamente el prompt
      event.preventDefault();
      // Guardar el evento para poder dispararlo más tarde
      setDeferredPrompt(event);
      // Mostrar el prompt personalizado después de un breve delay
      setTimeout(() => {
        setIsVisible(true);
      }, 3000);
    };

    // Escuchar cuando la app es instalada
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsVisible(false);
      setDeferredPrompt(null);
      console.log('PWA instalada exitosamente');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();
    
    // Esperar a que el usuario responda al prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('Usuario aceptó la instalación');
    } else {
      console.log('Usuario rechazó la instalación');
    }
    
    // Limpiar el deferredPrompt
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Mostrar nuevamente después de 24 horas
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // No mostrar si ya está instalada o no es visible
  if (isInstalled || !isVisible) {
    return null;
  }

  // Para navegadores que no soportan PWA install prompt, no mostrar nada
  if (!isIOS && !deferredPrompt) {
    return null;
  }

  // Verificar si fue descartado recientemente
  const dismissedTime = localStorage.getItem('installPromptDismissed');
  if (dismissedTime) {
    const timeDiff = Date.now() - parseInt(dismissedTime);
    const hoursAgo = timeDiff / (1000 * 60 * 60);
    if (hoursAgo < 24) {
      return null;
    }
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      left: 20,
      right: 20,
      zIndex: 1000,
      maxWidth: 400,
      margin: '0 auto'
    }}>
      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          background: 'linear-gradient(135deg, #001529 0%, #002140 100%)',
          color: 'white',
          border: 'none'
        }}
        bodyStyle={{ padding: 16 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <Space>
            <MobileOutlined style={{ fontSize: 24, color: '#fff' }} />
            <Title level={5} style={{ color: '#fff', margin: 0 }}>
              Instalar MogoTax
            </Title>
          </Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleDismiss}
            style={{ color: '#fff', padding: 0, minWidth: 'auto' }}
          />
        </div>
        
        <Text style={{ color: '#fff', opacity: 0.9, fontSize: 14, display: 'block', marginBottom: 16 }}>
          {isIOS ? 'Instala la app en tu pantalla de inicio' : 'Instala la app para un acceso más rápido y una mejor experiencia'}
        </Text>
        
        {isIOS ? (
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
            <Steps
              direction="vertical"
              size="small"
              items={[
                {
                  title: <Text style={{ color: '#fff', fontSize: 12 }}>Toca el botón compartir</Text>,
                  description: <Text style={{ color: '#fff', opacity: 0.8, fontSize: 11 }}>
                    <ShareAltOutlined style={{ marginRight: 4 }} />
                    icono central safari
                  </Text>,
                },
                {
                  title: <Text style={{ color: '#fff', fontSize: 12 }}>Selecciona "Agregar a Inicio"</Text>,
                  description: <Text style={{ color: '#fff', opacity: 0.8, fontSize: 11 }}>
                    <PlusOutlined style={{ marginRight: 4 }} />
                    en el menú que aparece
                  </Text>,
                }
              ]}
            />
          </div>
        ) : (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleInstallClick}
            style={{
              background: '#fff',
              color: '#001529',
              border: 'none',
              borderRadius: 8,
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Instalar App
          </Button>
        )}
      </Card>
    </div>
  );
};

export default InstallPrompt;